import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { colors } from '../../colors';
import type {
  NumberInputWithUnitSize,
  NumberInputWithUnitStyleProps,
  NumberInputWithUnitVariant,
  StyledControlButtonProps,
  StyledNumberInputWithUnitProps,
  UnitPosition,
} from './types';

const getNumberInputWithUnitSizeStyles = (size: NumberInputWithUnitSize) => {
  switch (size) {
    case 'small':
      return css`
        height: 32px;
        padding: 6px 12px;
        font-size: 13px;
        line-height: 1.2;
      `;
    case 'large':
      return css`
        height: 48px;
        padding: 14px 16px;
        font-size: 16px;
        line-height: 1.3;
      `;
    case 'medium':
    default:
      return css`
        height: 40px;
        padding: 10px 14px;
        font-size: 14px;
        line-height: 1.25;
      `;
  }
};

const getNumberInputWithUnitVariantStyles = (
  variant: NumberInputWithUnitVariant,
  error: boolean = false,
  styleProps?: NumberInputWithUnitStyleProps | undefined
) => {
  const baseStyles = css`
    background-color: ${styleProps?.backgroundColor ?? colors.white};
    border: 1px solid
      ${error
        ? (styleProps?.errorBorderColor ?? colors.error)
        : (styleProps?.borderColor ?? colors.gray[300])};

    &:focus-within {
      border-color: ${error
        ? (styleProps?.errorBorderColor ?? colors.error)
        : (styleProps?.focusBorderColor ?? colors.cyan[500])};
      background-color: ${styleProps?.focusBackgroundColor ?? colors.white};
      box-shadow: 0 0 0 2px ${error ? `${colors.error}1A` : `${colors.cyan[200]}4D`};
    }

    &:hover:not(:focus-within) {
      border-color: ${error
        ? (styleProps?.errorBorderColor ?? colors.error)
        : (styleProps?.borderColor ?? colors.gray[400])};
    }
  `;

  switch (variant) {
    case 'filled':
      return css`
        ${baseStyles}
        background-color: ${styleProps?.backgroundColor ?? colors.gray[100]};
        border: 1px solid ${colors.transparent};

        &:focus-within {
          background-color: ${styleProps?.focusBackgroundColor ?? colors.white};
          border-color: ${error
            ? (styleProps?.errorBorderColor ?? colors.error)
            : (styleProps?.focusBorderColor ?? colors.cyan[500])};
        }

        &:hover:not(:focus-within) {
          background-color: ${styleProps?.backgroundColor ?? colors.gray[200]};
        }
      `;
    case 'outline':
    default:
      return baseStyles;
  }
};

const getControlButtonSizeStyles = (size: NumberInputWithUnitSize) => {
  switch (size) {
    case 'small':
      return css`
        width: 22px;
        height: 50%;
        font-size: 9px;
      `;
    case 'large':
      return css`
        width: 30px;
        height: 50%;
        font-size: 13px;
      `;
    case 'medium':
    default:
      return css`
        width: 26px;
        height: 50%;
        font-size: 11px;
      `;
  }
};

export const Container = styled.div<{
  $fullWidth?: boolean;
  $styleProps?: NumberInputWithUnitStyleProps | undefined;
  $unitPosition: UnitPosition;
  $error?: boolean;
}>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: stretch;
  position: relative;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  margin: ${({ $styleProps }) => $styleProps?.margin ?? '0'};
  transition: all 0.2s ease-in-out;
  border-radius: 5px;
  border: 1px solid
    ${({ $error, $styleProps }) =>
      $error
        ? ($styleProps?.errorBorderColor ?? colors.error)
        : ($styleProps?.borderColor ?? colors.gray[200])};

  &:focus-within {
    border-color: ${colors.primary[300]} !important;
    background-color: ${colors.white};
    outline: none;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

export const StyledInput = styled.input<StyledNumberInputWithUnitProps>`
  box-sizing: border-box;
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ $styleProps }) => $styleProps?.color ?? colors.gray[800]};
  font-size: ${({ $styleProps }) => $styleProps?.fontSize ?? 'inherit'};
  font-weight: 500;
  padding: ${({ $styleProps }) => $styleProps?.padding ?? 'inherit'};
  text-align: ${({ $hasPrefix, $hasSuffix }) => ($hasPrefix || $hasSuffix ? 'left' : 'left')};
  box-shadow: none !important;
  border: none !important;

  ${({ $unitPosition }) =>
    $unitPosition === 'left'
      ? css`
          border-radius: 0 4px 4px 0;
        `
      : css`
          border-radius: 4px 0 0 4px;
        `}

  ${({ $size = 'medium', $styleProps }) => {
    if ($styleProps?.padding) return '';
    return getNumberInputWithUnitSizeStyles($size);
  }}

  ${({ $hasPrefix }) =>
    $hasPrefix &&
    css`
      padding-left: 32px !important;
    `}

  ${({ $hasSuffix }) =>
    $hasSuffix &&
    css`
      padding-right: 32px !important;
    `}

  &::placeholder {
    color: ${({ $styleProps }) => $styleProps?.placeholderColor ?? colors.gray[400]};
    font-weight: 400;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: ${colors.gray[100]};
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

export const InputContainer = styled.div<StyledNumberInputWithUnitProps>`
  box-sizing: border-box;
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
`;

export const PrefixSuffix = styled.span<{
  $position: 'prefix' | 'suffix';
  $styleProps?: NumberInputWithUnitStyleProps | undefined;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ $styleProps }) => $styleProps?.color ?? colors.gray[500]};
  font-size: ${({ $styleProps }) => $styleProps?.fontSize ?? '14px'};
  font-weight: 500;
  pointer-events: none;
  z-index: 1;

  ${({ $position }) =>
    $position === 'prefix'
      ? css`
          left: 12px;
        `
      : css`
          right: 12px;
        `}
`;

export const ControlsWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0;
  z-index: 2;
  height: 100%;
`;

export const ControlButton = styled.button<StyledControlButtonProps>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  color: ${({ $styleProps }) => $styleProps?.color ?? colors.gray[600]};
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  font-weight: 500;

  ${({ $size }) => getControlButtonSizeStyles($size)}

  &:hover:not(:disabled) {
    color: ${({ $styleProps }) => $styleProps?.color ?? colors.primary[300]};
  }

  &:active:not(:disabled) {
    background-color: ${colors.gray[100]};
    transform: scale(0.98);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    background-color: ${colors.gray[100]};
  }

  &:focus {
    outline: 1px solid ${({ $styleProps }) => $styleProps?.focusBorderColor ?? colors.primary[300]};
    color: ${({ $styleProps }) => $styleProps?.color ?? colors.primary[300]};
  }

  &:last-child {
    border-top: none;
  }
`;